'use client'
import React from 'react'
import classNames from 'classnames'
import { Media, ButtonField } from '@/payload-types'
import { Label, LabelProps } from '@/ui/atoms/Label'
import { CMSImage } from '@/ui/atoms/CMSImage'
import { Icon } from '@/ui/atoms/Icon'
import { Dialog, DialogTrigger, DialogHeader, DialogTitle } from '@/ui/components/Dialog'
import { RenderPopUpBlocks } from '@/ui/blocks/RenderPopUpBlocks'
import { TypedLocale } from 'payload'
import dynamic from 'next/dynamic'

const DynamicDialogContent = dynamic(() => import('@/ui/components/Dialog').then((mod) => mod.DialogContent), {
  ssr: false,
})

export type NewsCardProps = {
  title: string | Record<string, string>
  description?: string | Record<string, string>
  image?: Media | string | null
  date?: Date | string | null
  labels?: Array<{
    title: string | Record<string, string>
    variant?: LabelProps['variant']
  }>
  category?: {
    title: string
    colorScheme: string
  }
  source?: string
  isExternal?: boolean
  className?: string
  appearance?: 'filled' | 'outlined'
  showLinkIcon?: boolean
  button?: ButtonField | null
  locale?: TypedLocale
}

export const NewsCard: React.FC<NewsCardProps> = ({
  title,
  description,
  image,
  date,
  labels,
  category,
  source,
  isExternal,
  appearance = 'filled',
  className: classNameFromProps,
  showLinkIcon,
  button,
  locale,
}) => {
  // Helper to get string value from potentially localized field
  const getString = (value: string | Record<string, string> | undefined | null): string => {
    if (!value) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'object' && value !== null) {
      // For localized objects, return the first available value
      const values = Object.values(value).filter(v => typeof v === 'string')
      return values[0] || ''
    }
    return ''
  }

  const hasPopup = button?.linkType === 'popup' && button?.popupbutton && locale
  const cardContent = (
    <div
      className={classNames(
        'flex flex-col rounded-md border border-white/10 bg-neutral-100 backdrop-blur-[4px] transition-all duration-200 group p-4 relative',
        showLinkIcon && 'canhover:hover:bg-blue-20',
        hasPopup && 'hover:cursor-pointer',
        classNameFromProps,
      )}
    >
      {/* Top row: Labels (left) and Date (right) */}
      <div className="flex items-start justify-between mb-4">
        {/* Labels */}
        <div className="inline-flex flex-wrap gap-2">
          {labels && labels.length > 0 && (
            <>
              {labels
                .filter((label) => label && label.title)
                .map((label, index) => {
                  const title = getString(label.title)
                  // Caller-supplied variant wins (DashboardNews: Content green, Partner orange,
                  // Event blue — Veronika 2026-09-16); the old Event/other rule is the fallback.
                  const variant = label.variant ?? (title === 'Event' ? 'blue' : 'orange')
                  return (
                    <Label
                      key={index}
                      title={title}
                      variant={variant}
                      size="small"
                    />
                  )
                })}
            </>
          )}
        </div>

        {/* Date */}
        {date && (
          <span className={classNames(
            "text-label-XS-regular text-text-faded",
            showLinkIcon && "canhover:group-hover:text-text-faded-inverted"
          )}>
            {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
      </div>

      {/* Title */}
      <h4 className={classNames(
        "text-body-S-semibold lg:text-body-M-semibold text-text-base",
        showLinkIcon && "canhover:group-hover:text-text-inverted"
      )}>
        {getString(title)}
      </h4>

      {/* Description */}
      {description && (
        <p className={classNames(
          "text-body-XS-regular text-text-subtle line-clamp-3",
          showLinkIcon && "canhover:group-hover:text-text-subtle-inverted pr-8"
        )}>
          {getString(description)}
        </p>
      )}

      {/* External link icon */}
      {showLinkIcon && (
        <div className="absolute bottom-4 right-4">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="canhover:group-hover:fill-text-faded-inverted fill-[#636A79] transition-all duration-200">
            <path d="M16.0039 9.414L7.39691 18.021L5.98291 16.607L14.5889 8H7.00391V6H18.0039V17H16.0039V9.414Z"/>
          </svg>
        </div>
      )}
    </div>
  )

  // If button has popup type, wrap in Dialog
  if (hasPopup && button.popupbutton) {
    const { title: popupTitle, layout } = button.popupbutton

    return (
      <Dialog>
        <DialogTrigger asChild>
          {cardContent}
        </DialogTrigger>
        <DynamicDialogContent>
          <DialogHeader>
            <DialogTitle>{popupTitle || ''}</DialogTitle>
          </DialogHeader>
          <RenderPopUpBlocks blocks={layout ?? []} locale={locale!} />
        </DynamicDialogContent>
      </Dialog>
    )
  }

  // Otherwise, return card content as-is (Card component handles Link wrapper for links)
  return cardContent
}
